import {once, mapValues, keyBy, compact, forEach} from "lodash";
import {AdobeDTM} from "modules/AdobeDTM";
import CONSTANTS from "modules/utils/constants";
import {useCallback, useEffect, useRef, useState, RefObject} from "react";
import {useLocation} from "react-router-dom";
import {useParams, useNavigate} from "react-router";
import {toggleBodyClass} from "modules/utils/dom_helpers";

export const useDigitParams = <T extends string[]>(...keys: T): Record<T[number], number> => {
	const navigate = useNavigate();
	const onlyDigits = /\d+/;
	const params = useParams<Record<string, string>>();

	const keysWithValue = compact(
		keys.map((key) => ({
			value: params[key],
			key,
		}))
	);

	const hasInvalidParams = keysWithValue.some(
		(routeParam) => routeParam.value && !onlyDigits.test(routeParam.value)
	);

	if (hasInvalidParams) {
		console.error(`The following params should be digit: ${keys.join(", ")}`);
	}

	useEffect(() => {
		if (hasInvalidParams) {
			navigate("../");
		}
	}, [hasInvalidParams]);

	const parsedValues = mapValues(keyBy(keysWithValue, "key"), (routeParam) => {
		return Number(routeParam.value);
	});

	return parsedValues as Record<T[number], number>;
};

export const useFixedBody = (isOpen: boolean, name: string) => {
	useEffect(() => {
		toggleBodyClass("fixed", isOpen, name);
		return () => {
			toggleBodyClass("fixed", false, name);
		};
	}, [isOpen, name]);
};

export const usePrevious = <T = undefined>(value: T): T | undefined => {
	const ref = useRef<T>();

	useEffect(() => {
		ref.current = value;
	});

	return ref.current;
};

export const useMediaQuery = (query: string) => {
	const mediaMatch = window.matchMedia(query);
	const [matches, setMatches] = useState(mediaMatch.matches);

	useEffect(() => {
		const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
		mediaMatch.addEventListener("change", handler);

		return () => {
			mediaMatch.removeEventListener("change", handler);
		};
	});

	return matches;
};

export const useIsMobile = () => {
	return useMediaQuery(CONSTANTS.MOBILE_SIZE_QUERY);
};

export const useURLQuery = () => new URLSearchParams(useLocation().search);

export const useAdobeDTM = (trackStateParams?: Parameters<(typeof AdobeDTM)["trackState"]>[0]) => {
	useEffect(() => {
		if (trackStateParams) {
			AdobeDTM.trackState({
				...trackStateParams,
				"page.type": "beat the streak",
			});
		}
	}, []);
	/* eslint-disable-next-line @typescript-eslint/unbound-method */
	const trackOnce = useCallback(once(AdobeDTM.trackState), []);
	return {
		trackAction: (p?: Parameters<(typeof AdobeDTM)["trackAction"]>[0]) =>
			p &&
			AdobeDTM.trackAction({
				"action.element.targetUrl": window.location.href,
				"action.element.index": 0,
				...p,
			}),
		trackState: (p?: Parameters<(typeof AdobeDTM)["trackState"]>[0] | undefined) =>
			p &&
			AdobeDTM.trackState({
				"page.type": "beat the streak",
				...p,
			}),
		onceTrackState: trackOnce,
	};
};

export const useInertModal = (ref: RefObject<HTMLDivElement>, isOpen: boolean, name: string) => {
	const prevActive = useRef(document.activeElement);
	const modal = ref.current;

	useEffect(() => {
		const root = document.getElementById("root");

		const setInert = (node: ParentNode | null, isOpenState: boolean) => {
			forEach(node?.parentNode?.children, (child) => {
				if (child !== node && isOpenState) {
					child.setAttribute("inert", "true");
				} else {
					child.removeAttribute("inert");
				}
			});

			if (node && node?.parentNode !== root) {
				setInert(node.parentNode, isOpenState);
			}
		};

		if (modal && root) {
			setInert(modal, isOpen);
			if (isOpen) {
				prevActive.current = document.activeElement;
				modal.querySelector("button")?.focus();
			} else {
				const prevFocus = prevActive.current as HTMLElement;
				prevFocus?.focus();
			}
		}

		return () => {
			setInert(modal, false);
			const prevFocus = prevActive.current as HTMLElement;
			prevFocus?.focus();
		};
	}, [isOpen, name]);
};

export const useDialogModal = (ref: RefObject<HTMLDialogElement>, state: boolean) => {
	const activeElement = useRef<typeof document.activeElement>();
	const hasBeenFocused = useRef<boolean>(false);
	useEffect(() => {
		if (state) {
			try {
				if (!ref.current?.open) {
					activeElement.current = document.activeElement;
					ref.current?.showModal();
				}
				if (!hasBeenFocused.current) {
					ref.current?.querySelector("button")?.focus();
					hasBeenFocused.current = true;
				}
			} catch (e) {
				console.log(e);
			}
		} else {
			ref.current?.close();
			(activeElement.current as HTMLElement)?.focus?.();
		}

		return () => {
			(activeElement.current as HTMLElement)?.focus?.();
		};
	}, [state]);
};

export const useTrapFocus = (ref: RefObject<HTMLElement>, state: boolean) => {
	const activeElement = useRef<typeof document.activeElement>();
	const hasBeenFocused = useRef<boolean>(false);
	useEffect(() => {
		if (state) {
			try {
				if (!hasBeenFocused.current) {
					activeElement.current = document.activeElement;
					ref.current?.querySelector("button")?.focus();
					hasBeenFocused.current = true;
				}
			} catch (e) {
				console.log(e);
			}
		} else {
			(activeElement.current as HTMLElement)?.focus?.();
		}

		return () => {
			(activeElement.current as HTMLElement)?.focus?.();
		};
	}, [state]);
};
